// @ts-nocheck
/**
 * Ludipek B2B access-request form submission.
 *
 * - Market select (Chile/Perú): defaults to the detected country; switching it swaps the
 *   tax-id label (RUT/RUC), example placeholder, keyboard, and checksum.
 * - Business type select: required here to qualify the lead. The API keeps it optional so
 *   a cached bundle without it still posts, and rejects a value outside its list.
 * - Client-side tax-id validation: Chilean RUT mod-11 and Peruvian RUC Sunat checksum,
 *   lenient about dots, spaces, and the RUT hyphen (the API accepts the same formats).
 * - Honeypot field ("website") is dropped on the server if populated.
 * - POST to {apiBaseUrl}/customers/request-access — the API validates the tax id and
 *   emails the sales team. Does NOT create a Shopify customer or grant access: sales
 *   reviews the request, creates the NetSuite customer, and the nightly company sync
 *   then unlocks the store for that company.
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  if (BOOTSTRAP === null) return;

  const form = document.getElementById('b2b-request-access-form');
  if (form === null) return;

  const submitButton = form.querySelector('button[type="submit"]');
  const banner = form.querySelector('[data-banner]');
  const successPanel = document.querySelector('[data-success]');
  const FETCH_TIMEOUT_MS = 15_000;

  // Live character counter for the optional message. The textarea's maxlength is the hard cap; this
  // just shows progress toward it (reads maxLength so the limit lives in one place, the markup).
  const messageInput = form.querySelector('[data-message-input]');
  const messageCounter = form.querySelector('[data-message-counter]');
  if (messageInput && messageCounter) {
    const messageMax = messageInput.maxLength > 0 ? messageInput.maxLength : 500;
    const updateMessageCounter = () => {
      messageCounter.textContent = `${messageInput.value.length} / ${messageMax}`;
    };
    messageInput.addEventListener('input', updateMessageCounter);
    updateMessageCounter();
  }

  function message(key, fallback) {
    return (BOOTSTRAP.messages && BOOTSTRAP.messages[key]) || fallback;
  }

  // Server 422 codes → the same locale message keys the client uses for its own checks, so a
  // rejection that slips past the client (client/server drift, autofill) shows a localized message
  // instead of the English fallback the server carries.
  const SERVER_ERROR_MESSAGE_KEYS = {
    email_invalid: 'validationEmailInvalid',
    phone_invalid: 'validationPhoneInvalid',
    message_too_long: 'validationMessageTooLong',
    tax_id_invalid_cl: 'validationTaxIdInvalidCl',
    tax_id_invalid_pe: 'validationTaxIdInvalidPe',
    // FastAPI's own schema errors, keyed by their `type`. `enum` can only be business_type,
    // the one enum in the request model: a value the API does not know yet.
    missing: 'validationRequired',
    string_too_short: 'validationRequired',
    enum: 'validationBusinessTypeInvalid',
  };

  function showError(name, text) {
    const field = form.querySelector(`[name="${name}"]`);
    if (field === null) return;
    field.setAttribute('aria-invalid', 'true');
    const wrapper = field.closest('.request-access__field');
    if (wrapper !== null) wrapper.setAttribute('data-invalid', 'true');
    const errorEl = form.querySelector(`[data-error-for="${name}"]`);
    if (errorEl !== null) errorEl.textContent = text;
  }

  function clearError(name) {
    const field = form.querySelector(`[name="${name}"]`);
    if (field === null) return;
    field.setAttribute('aria-invalid', 'false');
    const wrapper = field.closest('.request-access__field');
    if (wrapper !== null) wrapper.removeAttribute('data-invalid');
    const errorEl = form.querySelector(`[data-error-for="${name}"]`);
    if (errorEl !== null) errorEl.textContent = '';
  }

  function clearAllErrors() {
    form.querySelectorAll('[data-invalid] [name]').forEach((field) => {
      field.setAttribute('aria-invalid', 'false');
    });
    form.querySelectorAll('[data-invalid]').forEach((el) => el.removeAttribute('data-invalid'));
    form.querySelectorAll('[data-error-for]').forEach((el) => {
      el.textContent = '';
    });
  }

  // Clear a field's error as soon as the user starts fixing it.
  ['input', 'change'].forEach((eventName) => {
    form.addEventListener(eventName, (event) => {
      const field = event.target;
      if (!field || !field.name) return;
      const wrapper = field.closest('.request-access__field');
      if (wrapper !== null && wrapper.hasAttribute('data-invalid')) {
        clearError(field.name);
      }
    });
  });

  // Market choice. The select defaults to the detected country, but geolocation misplaces
  // prospects (a Peruvian company browsing from Chile), so the applicant can switch it. The
  // tax-id field takes its label, example placeholder, and keyboard from the selected
  // <option>'s data attributes (rendered by the section), and validateFields reads the same
  // select through the form data, so the checksum follows it too.
  const countrySelect = form.querySelector('[data-country-select]');
  const taxIdInput = form.querySelector('[name="tax_id"]');
  // Not [data-tax-id-label]: the <option>s carry that attribute and come first in the DOM.
  const taxIdLabel = form.querySelector('[data-tax-id-field-label]');

  function applySelectedCountry() {
    const option = countrySelect.options[countrySelect.selectedIndex];
    if (!option) return;
    taxIdLabel.textContent = option.dataset.taxIdLabel || taxIdLabel.textContent;
    taxIdInput.placeholder = option.dataset.taxIdPlaceholder || '';
    taxIdInput.inputMode = option.dataset.taxIdInputmode || 'text';
    // An error computed against the other market's format no longer applies.
    clearError('tax_id');
  }

  if (countrySelect !== null && taxIdInput !== null && taxIdLabel !== null) {
    countrySelect.addEventListener('change', applySelectedCountry);
    // Re-sync in case the browser restored an earlier selection over the rendered default.
    applySelectedCountry();
  }

  /**
   * Validate a Chilean RUT (e.g. "12.345.678-5") using the mod-11 algorithm, accepting the
   * same formats as the API (domain/request_access.py): dots and spaces are ignored and the
   * hyphen before the check digit (always the last character) is optional, so "123456785"
   * and "12345678 5" pass too. The check digit is computed by multiplying each digit
   * (right-to-left) by weights cycling 2..7, summing, then 11 - (sum mod 11).
   * Result 11 → '0', 10 → 'K'.
   */
  function isValidRut(raw) {
    if (typeof raw !== 'string') return false;
    const match = raw.replace(/[.\s]/g, '').toUpperCase().match(/^(\d{7,8})-?([0-9K])$/);
    if (match === null) return false;
    const [, body, verifier] = match;
    let sum = 0;
    let multiplier = 2;
    for (let index = body.length - 1; index >= 0; index -= 1) {
      sum += Number(body[index]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    const remainder = 11 - (sum % 11);
    const expected = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);
    return expected === verifier;
  }

  /**
   * Validate a Peruvian RUC using Sunat's weighted-checksum algorithm, ignoring the spaces,
   * dots, and hyphens people type inside it (as the API does).
   * RUC is 11 digits starting with 10, 15, 17, or 20. Weights are
   * [5,4,3,2,7,6,5,4,3,2] applied to the first 10 digits; check digit is
   * (11 - (sum mod 11)) mod 10, so 10 becomes 0 and 11 becomes 1 (the reverse of the RUT's mapping).
   */
  function isValidRuc(raw) {
    if (typeof raw !== 'string') return false;
    const cleaned = raw.replace(/[\s.-]/g, '');
    if (!/^\d{11}$/.test(cleaned)) return false;
    const prefix = cleaned.slice(0, 2);
    if (!['10', '15', '17', '20'].includes(prefix)) return false;
    const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let index = 0; index < 10; index += 1) {
      sum += Number(cleaned[index]) * weights[index];
    }
    const expected = (11 - (sum % 11)) % 10;
    return expected === Number(cleaned[10]);
  }

  /**
   * Lenient/international phone check mirroring the server (domain/request_access.py): allow digits and
   * common formatting characters, and require 8-15 digits so landlines and foreign numbers pass.
   */
  function isValidPhone(raw) {
    if (typeof raw !== 'string') return false;
    if (!/^[+\d\s().-]+$/.test(raw)) return false;
    const digitCount = raw.replace(/\D/g, '').length;
    return digitCount >= 8 && digitCount <= 15;
  }

  function validateFields(data) {
    let ok = true;
    clearAllErrors();
    const required = ['company', 'business_type', 'tax_id', 'contact_name', 'email', 'phone'];
    for (const field of required) {
      if (!data[field] || String(data[field]).trim() === '') {
        showError(field, message('validationRequired', 'This field is required.'));
        ok = false;
      }
    }
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      showError('email', message('validationEmailInvalid', 'Enter a valid email.'));
      ok = false;
    }
    if (data.phone && !isValidPhone(data.phone)) {
      showError('phone', message('validationPhoneInvalid', 'Enter a valid phone number.'));
      ok = false;
    }
    const country = (data.store_country || '').toUpperCase();
    if (data.tax_id) {
      if (country === 'CL' && !isValidRut(data.tax_id)) {
        showError('tax_id', message('validationTaxIdInvalidCl', 'Invalid RUT.'));
        ok = false;
      } else if (country === 'PE' && !isValidRuc(data.tax_id)) {
        showError('tax_id', message('validationTaxIdInvalidPe', 'Invalid RUC.'));
        ok = false;
      }
    }
    return ok;
  }

  function collectFormData() {
    const formData = new FormData(form);
    const data = {};
    formData.forEach((value, key) => {
      data[key] = typeof value === 'string' ? value.trim() : value;
    });
    return data;
  }

  function showBanner(text) {
    if (banner === null) return;
    banner.textContent = text;
    // Offer a direct escape hatch: mail the sales team. The anchor is built via
    // DOM APIs (never innerHTML with the message string) to avoid injection.
    if (BOOTSTRAP.salesEmail) {
      banner.appendChild(document.createTextNode(' '));
      const mailLink = document.createElement('a');
      mailLink.href = `mailto:${BOOTSTRAP.salesEmail}`;
      mailLink.textContent = BOOTSTRAP.salesEmail;
      banner.appendChild(mailLink);
    }
    banner.hidden = false;
  }

  function hideBanner() {
    if (banner !== null) banner.hidden = true;
  }

  // Bring the confirmation into view and move focus to it, so a screen reader reads it and a
  // phone user is not left looking at the requirements above the form.
  function showSuccess() {
    if (successPanel === null) return;
    successPanel.hidden = false;
    form.hidden = true;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    successPanel.focus({ preventScroll: true });
    successPanel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  }

  function setBusy(busy) {
    if (submitButton === null) return;
    if (busy) {
      submitButton.disabled = true;
      submitButton.textContent = message('requestAccessSubmitting', 'Sending…');
    } else {
      submitButton.disabled = false;
      submitButton.textContent = submitButton.getAttribute('data-submit-label') || 'Send request';
    }
  }

  /**
   * Map a 422 response body onto inline field errors.
   *
   * The API sends two shapes of detail entry: its own checks send {field, code, message}
   * (see entrypoints/request_access.py), and FastAPI's schema validation sends
   * {loc: ['body', field], type, msg} (a missing field, an unknown business type). Either way
   * the code (or type) is mapped to a locale string via SERVER_ERROR_MESSAGE_KEYS so the message
   * stays in the storefront's language; the server-authored English message is the fallback when
   * the code is unknown. An entry only counts as mapped when the field has a visible error slot
   * ([data-error-for]) — errors on hidden fields must fall through to the generic banner or the
   * user gets no feedback at all. Returns true when at least one entry rendered somewhere visible.
   */
  async function mapServerFieldErrors(response) {
    let detail = null;
    try {
      detail = (await response.json()).detail;
    } catch (error) {
      return false;
    }
    if (!Array.isArray(detail)) return false;
    let mapped = 0;
    for (const entry of detail) {
      if (!entry) continue;
      const fromSchema = Array.isArray(entry.loc) && entry.loc[0] === 'body';
      const field = fromSchema ? entry.loc[1] : entry.field;
      const code = fromSchema ? entry.type : entry.code;
      const serverText = fromSchema ? entry.msg : entry.message;
      if (typeof field !== 'string') continue;
      if (form.querySelector(`[name="${field}"]`) === null) continue;
      if (form.querySelector(`[data-error-for="${field}"]`) === null) continue;
      const localeKey = typeof code === 'string' ? SERVER_ERROR_MESSAGE_KEYS[code] : undefined;
      // FastAPI's messages are generic English ("Input should be…"); without a locale string for
      // the type, the localized banner says more than they would.
      if (fromSchema && localeKey === undefined) continue;
      const fallback = typeof serverText === 'string' ? serverText : '';
      const text = localeKey ? message(localeKey, fallback) : fallback;
      if (text === '') continue;
      showError(field, text);
      mapped += 1;
    }
    return mapped > 0;
  }

  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideBanner();
    const data = collectFormData();

    // Honeypot: a real user leaves this field empty. Silently succeed to fool bots.
    if (data.website && String(data.website).trim() !== '') {
      showSuccess();
      return;
    }
    delete data.website;

    if (!validateFields(data)) return;
    if (!BOOTSTRAP.apiBaseUrl) {
      console.error(
        '[b2b-request-access] settings.ludipek_api_base_url is not configured — submission disabled'
      );
      showBanner(message('requestAccessFailed', 'We could not send your request.'));
      return;
    }

    const url = `${BOOTSTRAP.apiBaseUrl.replace(/\/$/, '')}/customers/request-access`;
    setBusy(true);
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': BOOTSTRAP.apiShopifyKey,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        if (response.status === 422 && (await mapServerFieldErrors(response))) {
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      showSuccess();
    } catch (error) {
      console.error('[b2b-request-access] submission failed', error);
      showBanner(message('requestAccessFailed', 'We could not send your request.'));
    } finally {
      setBusy(false);
    }
  });
})();
